import { useState } from 'react';
import { Node } from 'reactflow';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Play, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useWorkflowStore } from '@/stores/workflow-store';
import { NodeTestingService } from '@/services/node-testing.service';

interface TestNodeDialogProps {
  node: Node;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TestNodeDialog({ node, open, onOpenChange }: TestNodeDialogProps) {
  const { currentWorkflowId } = useWorkflowStore();
  
  // Get default input based on node type
  const getDefaultInput = () => {
    const nodeType = node.data?.nodeType || node.type;
    
    // For chat trigger nodes, provide a sample message
    if (nodeType === 'chat-trigger') {
      return JSON.stringify({
        message: 'Hello, this is a test message'
      }, null, 2);
    }
    
    // For loop nodes, provide sample array data
    if (nodeType === 'loop') {
      const inputDataPath = node.data?.parameters?.inputData || 'data';
      return JSON.stringify({
        [inputDataPath]: [
          { id: 1, name: 'Item 1' },
          { id: 2, name: 'Item 2' },
          { id: 3, name: 'Item 3' }
        ]
      }, null, 2);
    }
    
    // For AI agent nodes, check if they reference other nodes
    if (nodeType === 'ai-agent' || nodeType === 'aiAgent') {
      const userPrompt = node.data?.parameters?.userPrompt || '';
      
      // Check if the prompt contains $node references
      const nodeReferences = userPrompt.match(/\$node\["([^"]+)"\]/g);
      
      if (nodeReferences) {
        // Extract unique node names
        const nodeNames = [...new Set(nodeReferences.map((ref: string) => {
          const match = ref.match(/\$node\["([^"]+)"\]/);
          return match ? match[1] : null;
        }).filter(Boolean))];
        
        // Create sample data for each referenced node
        const sampleData: any = {};
        nodeNames.forEach((nodeName: string) => {
          // Provide sample data based on common patterns
          if (nodeName.toLowerCase().includes('loop')) {
            sampleData[nodeName] = {
              item: {
                id: 1,
                name: 'Sample Item',
                clean_transcript: 'This is a sample transcript for testing. The customer was very happy with the service.',
                // Add more sample fields as needed
              }
            };
          } else {
            sampleData[nodeName] = {
              data: 'Sample data from ' + nodeName
            };
          }
        });
        
        return JSON.stringify(sampleData, null, 2);
      }
    }
    
    return '{}';
  };
  
  const [inputData, setInputData] = useState(getDefaultInput());
  const [output, setOutput] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [executionTime, setExecutionTime] = useState<number | null>(null);

  const handleTest = async () => {
    if (!currentWorkflowId) {
      toast.error('No workflow selected');
      return;
    }

    setIsLoading(true);
    setError(null);
    setOutput(null);
    setExecutionTime(null);

    const startTime = Date.now();

    try {
      // Parse input data
      let parsedInput = {};
      try {
        parsedInput = JSON.parse(inputData);
      } catch (e) {
        throw new Error('Invalid JSON input');
      }

      // Convert the input data to the format expected by the backend
      // The backend expects an array of { nodeId, label, data } objects
      const previousNodeOutputs = Object.entries(parsedInput).map(([nodeName, data]) => ({
        nodeId: nodeName,
        label: nodeName,
        data: data
      }));

      // Call backend API to test the node using NodeTestingService
      const testParams = {
        nodeId: node.id,
        nodeType: node.data?.nodeType || node.type,
        nodeData: node.data || {},
        inputData: previousNodeOutputs,
        workflowId: currentWorkflowId,
      };
      
      const result = await NodeTestingService.testNode(testParams);

      const endTime = Date.now();
      setExecutionTime(endTime - startTime);

      setOutput(result.output);
      toast.success('Node tested successfully');
    } catch (err: any) {
      setError(err.message || 'An error occurred');
      toast.error('Node test failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setInputData(getDefaultInput());
    setOutput(null);
    setError(null);
    setExecutionTime(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Test Node: {node.data?.label || node.id}</DialogTitle>
          <DialogDescription>
            Test this node with custom input data and see the output
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Node Info */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="font-medium">Node ID:</span> {node.id}
            </div>
            <div>
              <span className="font-medium">Type:</span> {node.data?.nodeType || node.type}
            </div>
          </div>

          {/* Input Data */}
          <div className="space-y-2">
            <Label htmlFor="input-data">
              {(node.data?.nodeType || node.type) === 'chat-trigger' 
                ? 'Test Message Data (JSON)' 
                : 'Mock Previous Node Outputs (JSON)'}
            </Label>
            <p className="text-xs text-muted-foreground">
              {(node.data?.nodeType || node.type) === 'chat-trigger'
                ? 'Provide a test message, sessionId, and userId for the chat trigger.'
                : 'Provide mock data for nodes referenced in this node\'s parameters. Use node names as keys (e.g., "Loop", "HTTP Request").'}
            </p>
            <Alert className="mb-2">
              <AlertDescription className="text-xs">
                {(node.data?.nodeType || node.type) === 'chat-trigger' ? (
                  <>
                    <strong>Chat Trigger:</strong> The <code className="bg-muted px-1 py-0.5 rounded">message</code> field is required and must be a non-empty string. 
                    You can also use the Chat Test Panel (chat icon on the node) for a better testing experience.
                  </>
                ) : (
                  <>
                    <strong>Tip:</strong> If your node references variables like <code className="bg-muted px-1 py-0.5 rounded">{'{{ $node["Loop"].item.field }}'}</code>, 
                    provide mock data with the node name as the key: <code className="bg-muted px-1 py-0.5 rounded">{'{"Loop": {"item": {"field": "value"}}}'}</code>
                  </>
                )}
              </AlertDescription>
            </Alert>
            <Textarea
              id="input-data"
              value={inputData}
              onChange={(e) => setInputData(e.target.value)}
              placeholder='{"NodeName": {"field": "value"}}'
              className="font-mono text-sm min-h-[120px]"
              disabled={isLoading}
            />
          </div>

          {/* Output */}
          {output && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                <Label>Output</Label>
                {executionTime && (
                  <span className="text-xs text-muted-foreground">
                    ({executionTime}ms)
                  </span>
                )}
              </div>
              <pre className="bg-muted p-4 rounded-md overflow-x-auto text-sm">
                {JSON.stringify(output, null, 2)}
              </pre>
            </div>
          )}

          {/* Error */}
          {error && (
            <Alert variant="destructive">
              <XCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            Close
          </Button>
          <Button onClick={handleTest} disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Testing...
              </>
            ) : (
              <>
                <Play className="mr-2 h-4 w-4" />
                Run Test
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
